// PurchaseClaimForm.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import useFetch from "../../../../hooks/useFetch";
import QRScannerModal from '../../utils/QRScannerModal';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const validateTUCode = (code) => /^TU[A-Z0-9]{12}$/.test(code);

const generateBillNo = () => {
  const now = new Date();
  const yy  = String(now.getFullYear()).slice(2);
  const mm  = String(now.getMonth() + 1).padStart(2, "0");
  const dd  = String(now.getDate()).padStart(2, "0");
  const hh  = String(now.getHours()).padStart(2, "0");
  const mi  = String(now.getMinutes()).padStart(2, "0");
  const ss  = String(now.getSeconds()).padStart(2, "0");
  return `${yy}${mm}${dd}${hh}${mi}${ss}`;
};

const MAX_VERIFY_ATTEMPTS = 3;

// ─── Component ───────────────────────────────────────────────────────────────

const PurchaseClaimForm = ({ onSuccess }) => {
  const authState  = useSelector((s) => s.authReducer);
  const [fetchData] = useFetch();
  const token = authState?.token;
  const user  = authState?.user;

  // ── Form state ──────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    shopkeeperCode: "",
    shopkeeperName: "",
    shopName:       "",
    billNo:         generateBillNo(),
    billDate:       new Date().toISOString().split("T")[0],
    billAmount:     "",
  });

  // ── Merchant verify state ────────────────────────────────────────────────────
  const [verifying,        setVerifying]        = useState(false);
  const [merchantVerified, setMerchantVerified] = useState(false);
  const [verifyError,      setVerifyError]      = useState("");
  const [attemptsLeft,     setAttemptsLeft]     = useState(MAX_VERIFY_ATTEMPTS);
  const [merchantId,       setMerchantId]       = useState("");

  // ── Favourites state ─────────────────────────────────────────────────────────
  const [showFavourites,   setShowFavourites]   = useState(false);
  const [favourites,       setFavourites]       = useState([]);
  const [favLoading,       setFavLoading]       = useState(false);
  const [saveToFav,        setSaveToFav]        = useState(false);
  const [alreadyFav,       setAlreadyFav]       = useState(false);
  const [favMsg,           setFavMsg]           = useState("");

  // ── Submit state ─────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // ── Derived ──────────────────────────────────────────────────────────────────
  const expectedPoints = useMemo(
    () => Math.floor(Number(form.billAmount || 0) * 0.1),
    [form.billAmount]
  );

  const canSubmit = merchantVerified && !loading && message?.type !== "success";

  const [showQRScanner, setShowQRScanner] = useState(false);

  // const handleQRScanSuccess = (scannedData) => {
  //   setForm(prev => ({
  //     ...prev,
  //     shopkeeperCode: scannedData.shopkeeperCode,
  //     shopkeeperName: scannedData.shopkeeperName,
  //     shopName: scannedData.shopName,
  //     // Optional: store location & timestamp
  //     merchantLat: scannedData.latitude,
  //     merchantLong: scannedData.longitude,
  //     qrScannedAt: scannedData.scannedAt,
  //   }));

  //   // Optional: Auto trigger verify
  //   // verifyMerchant();
    
  //   setShowQRScanner(false);
  // };
  const handleQRScanSuccess = (scannedData) => {
    setForm(prev => ({
      ...prev,
      shopkeeperCode: scannedData.shopkeeperCode,
      // shopkeeperName / shopName intentionally NOT set here —
      // they must come from the backend after Verify, not from the QR itself.
      scanLat: scannedData.scanLatitude,
      scanLong: scannedData.scanLongitude,
      qrScannedAt: scannedData.scannedAt,
    }));
    // code changed via scan — any prior verification is now stale
    setMerchantVerified(false);
    setVerifyError(null);
    setShowQRScanner(false);
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setMessage(null);
  };

  // Reset verify state when code is manually changed after verification
  const handleCodeChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 16);
    setForm((p) => ({ ...p, shopkeeperCode: value }));
    setMerchantVerified(false);
    setVerifyError("");
    setAlreadyFav(false);
    setSaveToFav(false);
    setMerchantId("");
    setMessage(null);
  };

  // ── Verify Merchant ──────────────────────────────────────────────────────────
  const verifyMerchant = async () => {
    if (attemptsLeft <= 0) return;

    setVerifyError("");
    setMerchantVerified(false);

    if (!validateTUCode(form.shopkeeperCode)) {
      setVerifyError("Invalid TU Code — must be 14 characters starting with TU");
      return;
    }

    try {
      setVerifying(true);
      const res = await fetchData({
        url:     "/auth/verifyreferralcode",
        method:  "post",
        headers: { Authorization: token },
        data:    { think5whyscode: form.shopkeeperCode },
      });

      if (res?.status) {
        const t5w = res.t5wcode;
        setForm((p) => ({
          ...p,
          shopkeeperName: t5w.name    || "",
          shopName:       t5w.remarks || "",   // remarks = shop name on merchant user
        }));
        setMerchantId(t5w._id || "");
        setMerchantVerified(true);
        setVerifyError("");

        // Check if already in favourites
        checkIfAlreadyFav(form.shopkeeperCode);
      } else {
        const remaining = attemptsLeft - 1;
        setAttemptsLeft(remaining);
        setVerifyError(
          remaining > 0
            ? `Merchant not found. ${remaining} attempt${remaining === 1 ? "" : "s"} remaining.`
            : "❌ Maximum attempts reached. Please check the code and try again later."
        );
      }
    } catch {
      const remaining = attemptsLeft - 1;
      setAttemptsLeft(remaining);
      setVerifyError(
        remaining > 0
          ? `Verification failed. ${remaining} attempt${remaining === 1 ? "" : "s"} remaining.`
          : "❌ Maximum attempts reached."
      );
    } finally {
      setVerifying(false);
    }
  };

  // ── Favourites ───────────────────────────────────────────────────────────────

  const checkIfAlreadyFav = async (code) => {
    try {
      const res = await fetchData({
        url:     "/pd/favourites/check",
        method:  "post",
        headers: { Authorization: token },
        data:    { favouriteCode: code, favouriteType: "MERCHANT" },
      });
      setAlreadyFav(res?.exists || false);
      setSaveToFav(false);
    } catch {
      setAlreadyFav(false);
    }
  };

  const fetchFavourites = async () => {
    try {
      setFavLoading(true);
      const res = await fetchData({
        url:     "/pd/favourites/my",
        method:  "post",
        headers: { Authorization: token },
        data:    { favouriteType: "MERCHANT" },
      });
      setFavourites(res?.data || []);
    } catch {
      setFavourites([]);
    } finally {
      setFavLoading(false);
    }
  };

  const handleFavouriteSelect = (fav) => {
    setForm((p) => ({
      ...p,
      shopkeeperCode: fav.favouriteCode,
      shopkeeperName: fav.favouriteName,
      shopName:       fav.favouriteRemarks,
    }));
    setMerchantVerified(true);
    setAlreadyFav(true);
    setSaveToFav(false);
    setVerifyError("");
    setAttemptsLeft(MAX_VERIFY_ATTEMPTS);
    setShowFavourites(false);
  };

  const handleSaveFavourite = async () => {
    if (!merchantVerified || !form.shopkeeperCode) return;
    try {
      const res = await fetchData({
        url:     "/pd/favourites/add",
        method:  "post",
        headers: { Authorization: token },
        data:    {
          favouriteCode:    form.shopkeeperCode,
          favouriteName:    form.shopkeeperName,
          favouriteRemarks: form.shopName,
          favouriteType:    "MERCHANT",
        },
      });
      if (res?.success) {
        setAlreadyFav(true);
        setSaveToFav(false);
        setFavMsg("✅ Saved to favourites");
        setTimeout(() => setFavMsg(""), 3000);
      }
    } catch {
      setFavMsg("❌ Could not save");
      setTimeout(() => setFavMsg(""), 3000);
    }
  };

  // ── Submit ───────────────────────────────────────────────────────────────────

  const submitClaim = async () => {
    if (!form.shopkeeperCode || !form.billNo || !form.billDate || !form.billAmount) {
      setMessage({ type: "error", text: "Please fill all required fields" });
      return;
    }
    if (!merchantVerified) {
      setMessage({ type: "error", text: "Please verify the merchant code first" });
      return;
    }
    try {
      setLoading(true);
      setMessage(null);

      const res = await fetchData({
        url:    "/pd/streaks/claim-create",
        method: "post",
        data:   {
          customerId:     user?._id,
          customerCode:   user?.think5whyscode || "",
          customerName:   user?.name || "",
          shopkeeperId: merchantId,
          shopkeeperCode: form.shopkeeperCode,
          shopkeeperName: form.shopkeeperName,
          shopName:       form.shopName,
          billNo:         form.billNo,
          billDate:       form.billDate,
          billAmount:     Number(form.billAmount),
        },
        headers: { Authorization: token },
      });

      if (res?.success) {
        setMessage({ type: "success", text: "🎉 Claim submitted! Awaiting merchant approval." });
        setForm({
          shopkeeperCode: "",
          shopkeeperName: "",
          shopName:       "",
          billNo:         generateBillNo(),
          billDate:       new Date().toISOString().split("T")[0],
          billAmount:     "",
        });
        setMerchantVerified(false);
        setAlreadyFav(false);
        setSaveToFav(false);
        setAttemptsLeft(MAX_VERIFY_ATTEMPTS);
        onSuccess?.();
      } else {
        setMessage({ type: "warning", text: res?.message || "Unable to submit" });
      }
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || "Error submitting claim" });
    } finally {
      setLoading(false);
    }
  };

  // ── Styles ───────────────────────────────────────────────────────────────────

  const inputClass = "w-full mt-1 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/60 transition-colors";
  const labelClass = "text-[11px] text-gray-100 font-medium";

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="bg-[#111827] border-t border-cyan-200 rounded-xl p-4 space-y-3">

      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-cyan-500/20 flex items-center justify-center text-sm">🧾</div>
        <div>
          <div className="text-cyan-200 font-bold text-sm">Claim Your Purchase Streak</div>
          <div className="text-[10px] text-gray-400">Earn 10% of bill amount as streak points</div>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className={labelClass}>Merchant Think5Whys Code *</label>
            <div className="flex gap-2 mt-1">
                {/* QR Scan Button */}
                <button
                onClick={() => setShowQRScanner(true)}
                disabled={merchantVerified}
                className="w-fit px-2 py-1 rounded-xl text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white transition-all flex items-center gap-2"
                >
                  <i className="fas fa-qrcode text-xl lg:text-2xl"></i>
                  <span>Scan QR</span>
                </button>
                {/* Your Verify & Favourites buttons */}
            </div>

            {/* QR Scanner */}
            <QRScannerModal
                isOpen={showQRScanner}
                onClose={() => setShowQRScanner(false)}
                onScanSuccess={handleQRScanSuccess}
            />

          <div className="flex gap-2 mt-1">
            <input
              name="shopkeeperCode"
              value={form.shopkeeperCode}
              onChange={handleCodeChange}
              placeholder="TU243BM5850150"
              maxLength={14}
              style={{width: "200px"}}
              className={`w-32 flex-1 bg-gray-800 border ${
                merchantVerified
                  ? "border-green-500/60"
                  : verifyError
                  ? "border-red-500/60"
                  : "border-gray-700"
              } rounded-xl px-2 py-1 text-xs text-white placeholder-gray-500 focus:outline-none transition-colors tracking-widest`}
            />

            {/* Verify button */}
            <button
              onClick={verifyMerchant}
              disabled={verifying || attemptsLeft <= 0 || merchantVerified}
              className={`w-fit px-2 py-1 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${
                merchantVerified
                  ? "bg-green-600/30 text-green-300 border border-green-600/40 cursor-default"
                  : attemptsLeft <= 0
                  ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white active:scale-95"
              }`}
            >
              {verifying
                ? "..."
                : merchantVerified
                ? "✓ Verified"
                : `Verify${attemptsLeft < MAX_VERIFY_ATTEMPTS ? ` (${attemptsLeft})` : ""}`}
            </button>

            {/* My Favourites button */}
            <button
              onClick={() => {
                setShowFavourites((v) => !v);
                if (!showFavourites) fetchFavourites();
              }}
              className="w-fit px-2 py-1 rounded-lg text-xs font-bold bg-purple-700 hover:bg-purple-600 text-white active:scale-95 transition-all whitespace-nowrap"
            >
              ★ Favourites
            </button>
          </div>

          {/* Verify error */}
          {verifyError && (
            <p className="text-red-400 text-[10px] mt-1">{verifyError}</p>
          )}

          {/* Verified badge */}
          {merchantVerified && (
            <p className="text-green-400 text-[10px] mt-1">
              ✅ Merchant verified — {form.shopkeeperName}
            </p>
          )}
        </div>

        {/* ── Favourites Dropdown ── */}
        <AnimatePresence>
          {showFavourites && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-gray-800/80 border border-purple-700/40 rounded-xl overflow-hidden"
            >
              <div className="flex justify-between items-center px-3 py-2 border-b border-gray-700">
                <span className="text-xs font-bold text-purple-300">★ My Favourite Merchants</span>
                <button
                  onClick={() => setShowFavourites(false)}
                  className="w-fit px-2 py-1 text-gray-500 hover:text-white text-xs"
                >✕</button>
              </div>

              {favLoading ? (
                <div className="px-3 py-3 text-xs text-gray-400">Loading...</div>
              ) : favourites.length === 0 ? (
                <div className="px-3 py-3 text-xs text-gray-400">No favourite merchants saved yet.</div>
              ) : (
                <div className="divide-y divide-gray-700/50 max-h-48 overflow-y-auto">
                  {favourites.map((fav) => (
                    <button
                      key={fav._id}
                      onClick={() => handleFavouriteSelect(fav)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-700/60 transition-colors"
                    >
                      <div className="text-xs font-semibold text-white">
                        {fav.favouriteRemarks || fav.favouriteName}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        {fav.favouriteName} · {fav.favouriteCode}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Shop Name + Merchant Name ── */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Shop Name</label>
            <input
              name="shopName"
              value={form.shopName}
              onChange={handleChange}
              placeholder="Shop name"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Merchant Name</label>
            <input
              name="shopkeeperName"
              value={form.shopkeeperName}
              onChange={handleChange}
              placeholder="Owner name"
              className={inputClass}
            />
          </div>
        </div>

        {/* ── Save to Favourites checkbox (shown only after verification) ── */}
        {merchantVerified && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="saveToFav"
              checked={saveToFav}
              disabled={alreadyFav}
              onChange={(e) => {
                setSaveToFav(e.target.checked);
                if (e.target.checked) handleSaveFavourite();
              }}
              className="accent-purple-500 w-3.5 h-3.5"
            />
            <label
              htmlFor="saveToFav"
              className={`text-[11px] ${alreadyFav ? "text-gray-500 cursor-not-allowed" : "text-gray-300 cursor-pointer"}`}
            >
              {alreadyFav ? "★ Already in favourites" : "Save to Favourites"}
            </label>
            {favMsg && <span className="text-[10px] text-green-400">{favMsg}</span>}
          </div>
        )}

        {/* ── Bill Number (auto-generated, editable) + Bill Date ── */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>
              Bill Number *
              <span className="ml-1 text-gray-500 font-normal">(auto-generated)</span>
            </label>
            <input
              name="billNo"
              value={form.billNo}
              onChange={handleChange}
              placeholder="yymmddhhmmsss"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Bill Date *</label>
            <input
              type="date"
              name="billDate"
              value={form.billDate}
              onChange={handleChange}
              className={inputClass}
            />
          </div>
        </div>

        {/* ── Bill Amount ── */}
        <div className="flex flex-col">
          <label className={labelClass}>Bill Amount (₹) *</label>
          <input
            type="number"
            min="1"
            name="billAmount"
            value={form.billAmount}
            onChange={handleChange}
            placeholder="0"
            className={`${inputClass} !w-32`}
          />
        </div>
      </div>

      {/* ── Points Preview ── */}
      <AnimatePresence>
        {form.billAmount > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gradient-to-r from-cyan-950/60 to-purple-950/60 border border-cyan-500/20 rounded-xl p-3 flex items-center justify-between"
          >
            <div>
              <div className="text-[11px] text-gray-400">Expected reward</div>
              <div className="text-2xl font-black text-green-400">{expectedPoints}</div>
              <div className="text-[10px] text-gray-400">PurpleStreak Points</div>
            </div>
            <div className="text-3xl">⚡</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Message ── */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`p-3 rounded-xl text-xs font-bold ${
              message.type === "success"
                ? "bg-green-900/40 text-green-300 border border-green-700/40"
                : message.type === "warning"
                ? "bg-yellow-900/40 text-yellow-300 border border-yellow-700/40"
                : "bg-red-900/40 text-red-300 border border-red-700/40"
            }`}
          >
            {message.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Submit ── */}
      <button
        onClick={submitClaim}
        disabled={!canSubmit}
        className="w-fit px-2 py-1 rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-400 text-white font-black text-xs disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-transform"
      >
        {loading ? "Submitting..." : "Submit Purchase Claim →"}
      </button>

      {!merchantVerified && (
        <p className="text-[10px] text-gray-500">
          Verify the merchant code above to enable submission.
        </p>
      )}
    </div>
  );
};

export default PurchaseClaimForm;
